import { FC, FormEvent, useState } from 'react';
import { toast } from 'react-toastify';

import classNames from 'classnames';
import Cookies from 'js-cookie';

import { Spinner } from '@uikit';

import { EMAIL_SEND_DELAY, namedErrors, storageKeys } from '@shared/constants';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import { getFromUrl } from '@shared/helpers/url';
import { isEmail } from '@shared/helpers/validation';
import { useFeatureToggles } from '@shared/hooks/useFeatureToggles';

import { useSendPasswordlessEmailMutation } from './useSendPasswordlessEmailMutation';

export interface PasswordlessAuthProps {
  disabled?: boolean;
  className?: string;
}

const PasswordlessAuth: FC<PasswordlessAuthProps> = ({ disabled, className }) => {
  const featureToggles = useFeatureToggles();
  const [email, setEmail] = useState<string>('');
  const {
    mutateAsync: sendPasswordlessEmail,
    isError,
    isSuccess,
    isPending: isLoading,
    reset,
  } = useSendPasswordlessEmailMutation();

  if (!featureToggles.passwordlessAuth) {
    return null;
  }

  const sendEmail = async () => {
    try {
      if (!isEmail(email)) {
        throw new Error(namedErrors.INVALID_EMAIL_ADDRESS);
      }

      const referredBy = Cookies.get(storageKeys.REFERRAL);

      const from = getFromUrl();

      if (!from) {
        throw new Error('Unable to get url');
      }

      await sendPasswordlessEmail({ json: { from, email, referredBy } });

      toast.success('Sign in URL sent successfully to your email. Please check it out');

      Cookies.remove(storageKeys.REFERRAL);
    } catch (error) {
      displayErrorToast({ error });
    } finally {
      setTimeout(reset, EMAIL_SEND_DELAY);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    sendEmail();
  };

  const isDisabled = isLoading || disabled;

  return (
    <form onSubmit={handleSubmit} className={classNames('relative', className)}>
      <input
        disabled={isDisabled}
        className={classNames('text-field-base w-full pr-[8.125rem]', {
          'text-field-error': isError,
          'text-field-success': isSuccess,
          'text-field-disabled': isDisabled,
        })}
        id="passwordless-input"
        value={email}
        placeholder="Continue with Email"
        onChange={({ target: { value } }) => setEmail(value)}
      />

      <button
        disabled={isDisabled}
        type="submit"
        className="absolute right-1 top-1 bottom-1 button button-md rounded-lg button-solid-primary-200 min-w-[7.125rem]"
      >
        {isLoading && <Spinner size="sm" color="disabled" />}
        {!isLoading && 'Continue'}
      </button>
    </form>
  );
};

export default PasswordlessAuth;
